import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Service from "../../Service/http";
import { useQuery, useMutation } from 'react-query';
import Select from 'react-select';
import { RxCross2 } from "react-icons/rx";
import { toast } from "react-toastify";
import { tagOptions } from './tagUtil';
import { Autocomplete, TextField, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CodechefLogo from '../../../assets/codechef_logo.jpg';
import CodeforcesLogo from '../../../assets/codeforces_logo.webp';
import MentorpickLogo from '../../../assets/mentorpick.jpeg';
import LeetcodeLogo from '../../../assets/leetcode_logo.png';

export default function Problem() {
    const service = new Service();
    const { problemId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [nextProblem, setNextProblem] = useState('');
    const [previousProblem, setPreviousProblem] = useState('');
    const [similarProblem, setSimilarProblem] = useState('');
    const [matchingProblems, setMatchingProblems] = useState([]);
    const [nextProblems, setNextProblems] = useState([]);
    const [previousProblems, setPreviousProblems] = useState([]);
    const [similarProblems, setSimilarProblems] = useState([]);
    const [allTags, setAllTags] = useState([])
    const nextProblemRef = useRef(null);
    const previousProblemRef = useRef(null);
    const similarProblemRef = useRef(null);

    const NEXT_PROBLEM = 'NEXT_PROBLEM';
    const PREVIOUS_PROBLEM = 'PREVIOUS_PROBLEM';
    const SIMILAR_PROBLEM = 'SIMILAR_PROBLEM';
    
    const { data, isError, refetch } = useQuery(['problem', problemId], () =>
        service.get(`api/admin/problem/${problemId}`).then(response => response)
    );

    /**
     * Used for getting matching problems so that we can show
     * the matching problems to the user to choose next, previous
     * or similar problems
     */
    function getMatchingProblems() {
        const problemName = nextProblem || previousProblem || similarProblem;
        if(problemName !== null && problemName !== undefined && problemName.trim() !== ''){
            service.get(`api/problems/getMatchingProblems?problemName=${problemName}&limit=15`).then(response => {
                setMatchingProblems(response);
            });
        }
    }

    useQuery([nextProblem, previousProblem, similarProblem], getMatchingProblems, {refetchOnWindowFocus: false});

    useEffect(() => {
        const getAllNextProblems = async () => {
            const response = await service.get(`api/admin/graphProblem/nextProblem?problemId=${problemId}`);
            setNextProblems(response.data);
        }

        getAllNextProblems();
    }, []);

    useEffect(() => {
        const getAllPreviousProblems = async () => {
            const response = await service.get(`api/admin/graphProblem/previousProblem?problemId=${problemId}`);
            setPreviousProblems(response.data);
        }

        getAllPreviousProblems();
    }, []);

    useEffect(() => {
        const getAllSimilarProblems = async () => {
            const response = await service.get(`api/admin/graphProblem/similarProblem?problemId=${problemId}`);
            setSimilarProblems(response.data);
        }

        getAllSimilarProblems();
    }, []);

    const [formData, setFormData] = useState({
        score: '',
        problemId: '',
        siteName: '',
        problemName: '',
        rating: '',
        normalizedTag: [],
        editorial: [],
        authoredDate: '',
        contest: [],
    });

    const handleTagChange = (selectedOptions) => {
        setFormData({
            ...formData,
            normalizedTag: selectedOptions
        });
    };

    const handleEditorialChange = (selectedOptions) => {

        if (selectedOptions.key === 'Enter') {
            selectedOptions.preventDefault(); // Prevent the default form submission
            setFormData({
                ...formData,
                editorial: [...formData.editorial, selectedOptions.target.value]
            });
            selectedOptions.target.value = ''
        }
    }

    const handleEditorialDelete = (index) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            editorial: prevFormData.editorial.filter((_, i) => i !== index)
        }));
    };

    useEffect(() => {
        if (data && data.problem) {
            setFormData({
                score: data.problem.score,
                problemId: data.problem.problemId,
                siteName: data.problem.siteName,
                problemName: data.problem.problemName,
                editorial: data.problem.editorial,
                difficulty: data.problem.difficulty,
                rating: data.problem.rating,
                authoredDate: data.problem.authoredDate,    
                contest: data.problem.contest,
                normalizedTag: data.problem.normalizedTag,
            });
        }
    }, [data]);

    const updateProblemMutation = useMutation(updatedData =>
        service.put(`api/admin/problem/${problemId}`, updatedData)
    );

    const reFetchProblem = useMutation(updatedData =>
        service.put(`api/admin/problem/refetch/${problemId}`, updatedData)
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateProblemMutation.mutate(formData, {
            onSuccess: () => {
                refetch();
                toast.success('Problem Updated Successfully');
            },
            onError: () => {
                toast.error('Error updating problem');
            }
        });
    };


    const handleReFetchUpdate = (e) => {
        e.preventDefault();
        reFetchProblem.mutate({ problemId }, {
            onSuccess: () => {
                refetch();
                toast.success('Problem Re-fetched successfully');
            },
            onError: () => {
                toast.error('Error re-fetching problem');
            }
        });
    };

    useEffect(() => {

        const fetchAllTags = async () => {
            setAllTags(await tagOptions());
        }

        fetchAllTags();

    }, [])

    if (isError) {
        return <div className="alert alert-danger">Error loading problem</div>;
    }

    if (!data) {
        return <div>Loading...</div>;
    }

    /**
     * used to navigate to problem linking page
     */
    function linkProblem() {
        navigate("?selectedTab=problem-linking");
        setSearchParams({ selectedTab: "problem-linking"});
    }

    /**
     * used to navigate to problem details page
     */
    function editProblemDetails() {
        navigate("");
        setSearchParams();
    }

    const selectedTab = searchParams.get("selectedTab");

    function updateNextProblem(e) {
        setPreviousProblem('');
        setSimilarProblem('');
        setNextProblem(e.target.value);
    }

    function updatePreviousProblem(e) {
        setNextProblem('')
        setSimilarProblem('');
        setPreviousProblem(e.target.value);
    }

    function updateSimilarProblem(e) {
        setNextProblem('');
        setPreviousProblem('');
        setSimilarProblem(e.target.value);
    }

    /**
     * creating next problem relationship between 
     * current problem and selected problem
     */
    async function selectNextProblem(event, value) {
        try {
            setNextProblem('');
            setPreviousProblem('');
            setSimilarProblem('');
            if(previousProblemRef.current) {
                let clearButton = previousProblemRef.current.querySelector('[title="Clear"]');
                if (clearButton){
                    clearButton.click();
                }
            }
            if(similarProblemRef.current) {
                let clearButton = similarProblemRef.current.querySelector('[title="Clear"]');
                if (clearButton) {
                    clearButton.click();
                }
            }
            if(value !== null) {
                await service.post('api/problems/linkProblems', { problemOneId: problemId, problemTwoId: value._id, relation: NEXT_PROBLEM });
                const response = await service.get(`api/admin/graphProblem/nextProblem?problemId=${problemId}`);
                setNextProblems(response.data);
                toast.success('Next Problem Updated Successfully')
            }
        }
        catch(err) {
            console.error('Error while updating Next Problem', err);
            toast.error('Error while updating Next Problem');
        }
    }

    /**
     * creating previous problem relationship between 
     * current problem and selected problem
     */
    async function selectPreviousProblem(event, value) {
        try {
            setNextProblem('');
            setPreviousProblem('');
            setSimilarProblem('');
            if(nextProblemRef.current) {
                let clearButton = nextProblemRef.current.querySelector('[title="Clear"]');
                if (clearButton) {
                    clearButton.click();
                }
            }
            if(similarProblemRef.current) {
                let clearButton = similarProblemRef.current.querySelector('[title="Clear"]');
                if (clearButton) {
                    clearButton.click();
                }
            }
            if(value !== null) {
                await service.post('api/problems/linkProblems', { problemOneId: problemId, problemTwoId: value._id, relation: PREVIOUS_PROBLEM });
                const response = await service.get(`api/admin/graphProblem/previousProblem?problemId=${problemId}`)
                setPreviousProblems(response.data);
                toast.success('Updated Previous Problem Successfully');
            }
        }
        catch(err) {
            console.error('Error while updating previous problem', err);
            toast.error('Error while updating previous problem');
        }
    }

    /**
     * creating similar problem relationship between 
     * current problem and selected problem
     */
    async function selectSimilarProblem(event, value) {
        try {
            setNextProblem('');
            setPreviousProblem('');
            setSimilarProblem('');
            if(nextProblemRef.current) {
                let clearButton = nextProblemRef.current.querySelector('[title="Clear"]');
                if (clearButton) {
                    clearButton.click();
                }
            }
            if(previousProblemRef.current) {
                let clearButton = previousProblemRef.current.querySelector('[title="Clear"]');
                if (clearButton){
                    clearButton.click();
                }
            }
            if(value !== null) {
                await service.post('api/problems/linkProblems', { problemOneId: problemId, problemTwoId: value._id, relation: SIMILAR_PROBLEM });
                const response = await service.get(`api/admin/graphProblem/similarProblem?problemId=${problemId}`)
                setSimilarProblems(response.data);
                toast.success('Updated Similar Problem Successfully')
            }
        }
        catch(err) {
            console.error('Error while updating Similar Problem', err);
            toast.error('Error while updating Similar Problem');
        }
    }

    async function removeNextProblemRelationship(nextProblem) {
        try {
            let problemOneSlug = formData.problemId;
            let problemOneSiteName = formData.siteName;
            let problemTwoSlug = nextProblem.slug;
            let problemTwoSiteName = nextProblem.site;
            await service.post(`api/admin/graphProblem/removeRelationshipBetweenProblems`, 
                {
                    problemOneSlug, 
                    problemOneSiteName, 
                    problemTwoSlug, 
                    problemTwoSiteName, 
                    relationshipType: NEXT_PROBLEM
                }
            );
            await service.post(`api/admin/graphProblem/removeRelationshipBetweenProblems`, 
                {
                    problemOneSlug: problemTwoSlug, 
                    problemOneSiteName: problemTwoSiteName,
                    problemTwoSlug: problemOneSlug, 
                    problemTwoSiteName: problemOneSiteName, 
                    relationshipType: PREVIOUS_PROBLEM
                }
            );
            const response = await service.get(`api/admin/graphProblem/nextProblem?problemId=${problemId}`);
            setNextProblems(response.data);
            toast.success('Removed Next Problem');
        }
        catch(err) {
            console.log('Unable to remove the relationship to next problem', err)
            toast.error('Unable to remove Next Problem');
        }
    }

    async function removePreviousProblemRelationship(previousProblem) {
        try {
            let problemOneSlug = formData.problemId;
            let problemOneSiteName = formData.siteName;
            let problemTwoSlug = previousProblem.slug;
            let problemTwoSiteName = previousProblem.site;
            await service.post(`api/admin/graphProblem/removeRelationshipBetweenProblems`, 
                {
                    problemOneSlug, 
                    problemOneSiteName, 
                    problemTwoSlug, 
                    problemTwoSiteName, 
                    relationshipType: PREVIOUS_PROBLEM
                }
            );
            await service.post(`api/admin/graphProblem/removeRelationshipBetweenProblems`, 
                {
                    problemOneSlug: problemTwoSlug, 
                    problemOneSiteName: problemTwoSiteName,
                    problemTwoSlug: problemOneSlug, 
                    problemTwoSiteName: problemOneSiteName, 
                    relationshipType: NEXT_PROBLEM
                }
            );
            const response = await service.get(`api/admin/graphProblem/previousProblem?problemId=${problemId}`);
            setPreviousProblems(response.data);
            toast.success('Removed Previous Problem');
        }
        catch(err) {
            console.log('Unable to remove the relationship to previous problem', err)
            toast.error('Unable to remove Previous Problem');
        }
    }

    async function removeSimilarProblemRelationship(similarProblem) {
        try {
            let problemOneSlug = formData.problemId;
            let problemOneSiteName = formData.siteName;
            let problemTwoSlug = similarProblem.slug;
            let problemTwoSiteName = similarProblem.site;
            await service.post(`api/admin/graphProblem/removeRelationshipBetweenProblems`, 
                {
                    problemOneSlug, 
                    problemOneSiteName, 
                    problemTwoSlug, 
                    problemTwoSiteName, 
                    relationshipType: SIMILAR_PROBLEM
                }
            );
            await service.post(`api/admin/graphProblem/removeRelationshipBetweenProblems`, 
                {
                    problemTwoSlug: problemOneSlug,
                    problemTwoSiteName: problemOneSiteName,
                    problemOneSlug: problemTwoSlug,
                    problemOneSiteName: problemTwoSiteName,
                    relationshipType: SIMILAR_PROBLEM
                }
            )
            const response = await service.get(`api/admin/graphProblem/similarProblem?problemId=${problemId}`);
            setSimilarProblems(response.data);
            toast.success('Removed Similar Problem');
        }
        catch(err) {
            console.error('Unable to remove the similar problem relationship', err);
            toast.error('Unable to Remove Similar Problem');
        }
    }

    return (
        <div className="container mt-5">
            <div>
                <button onClick={editProblemDetails} className="m-1 btn btn-primary">Problem Details</button>
                <button onClick={linkProblem} className="btn btn-primary">Problem Linking</button>
            </div>
            {
                selectedTab 
                ?
                <div>
                    <div className="d-flex justify-content-between">
                        <div>
                            <h4>Next Problem</h4>
                            <Autocomplete
                                disablePortal
                                id="next-problem"
                                freeSolo={true}
                                ref={nextProblemRef}
                                options={matchingProblems}
                                getOptionLabel={option => (option.problemName + " | " + option.siteName + " | " + option.problemId)}
                                sx={{ width: 350, maxWidth: 350 }}
                                renderInput={(params) => <TextField {...params} label="Search Problem" onChange={updateNextProblem} />}
                                onChange={selectNextProblem}
                            />
                            <List sx={{ width: 350, maxWidth: 350 }}>
                            {
                                nextProblems.length > 0 && nextProblems?.map((problem, index) => {
                                    let siteLogo = null;
                                    if(problem.site === 'CODECHEF') {
                                        siteLogo = CodechefLogo;
                                    }
                                    else if(problem.site === 'CODEFORCES') {
                                        siteLogo = CodeforcesLogo;
                                    }
                                    else if(problem.site === 'MENTORPICK') {
                                        siteLogo = MentorpickLogo;
                                    }
                                    else if(problem.site === 'LEETCODE') {
                                        siteLogo = LeetcodeLogo;
                                    }
                                    return (
                                        <ListItem key={index} disablePadding sx={{ border: "1px solid #ccc"}}>
                                            <ListItemButton>
                                                <ListItemText primary={problem.title} primaryTypographyProps={{ style: {overflow: 'scroll' , whiteSpace: 'nowrap', marginRight: '10px'}}} />
                                                <img src={ siteLogo } alt="site_logo" width={"30px"} style={{marginRight:'10px'}}/>
                                                <DeleteIcon sx={{ color: 'red' }} onClick={() => removeNextProblemRelationship(problem)} />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })
                            }
                            </List>
                        </div>
                        <div>
                            <h4>Previous Problem</h4>
                            <Autocomplete
                                disablePortal
                                id="previous-problem"
                                freeSolo={true}
                                ref={previousProblemRef}
                                options={matchingProblems}
                                getOptionLabel={option => (option.problemName + " | " + option.siteName + " | " + option.problemId)}
                                sx={{ width: 350, maxWidth: 350 }}
                                renderInput={(params) => <TextField {...params} label="Search Problem" onChange={updatePreviousProblem} />}
                                onChange={selectPreviousProblem}
                            />
                            <List sx={{ width: 350, maxWidth: 350 }}>
                            {
                                previousProblems.length > 0 && previousProblems?.map((problem, index) => {
                                    let siteLogo = null;
                                    if(problem.site === 'CODECHEF') {
                                        siteLogo = CodechefLogo;
                                    }
                                    else if(problem.site === 'CODEFORCES') {
                                        siteLogo = CodeforcesLogo;
                                    }
                                    else if(problem.site === 'MENTORPICK') {
                                        siteLogo = MentorpickLogo;
                                    }
                                    else if(problem.site === 'LEETCODE') {
                                        siteLogo = LeetcodeLogo;
                                    }
                                    return (
                                        <ListItem key={index} disablePadding sx={{ border: "1px solid #ccc"}}>
                                            <ListItemButton>
                                                <ListItemText primary={problem.title} primaryTypographyProps={{ style: {overflow: 'scroll' , whiteSpace: 'nowrap', marginRight: '10px'}}} />
                                                <img src={ siteLogo } alt="site_logo" width={"30px"} style={{marginRight:'10px'}}/>
                                                <DeleteIcon sx={{ color: 'red' }} onClick={() => removePreviousProblemRelationship(problem)} />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })
                            }
                            </List>
                        </div>
                        <div>
                            <h4>Similar Problem</h4>
                            <Autocomplete
                                disablePortal
                                id="similar-problem"
                                freeSolo={true}
                                ref={similarProblemRef}
                                options={matchingProblems}
                                getOptionLabel={option => (option.problemName + " | " + option.siteName + " | " + option.problemId)}
                                sx={{ width: 350, maxWidth: 350 }}
                                renderInput={(params) => <TextField {...params} label="Search Problem" onChange={updateSimilarProblem} />}
                                onChange={selectSimilarProblem}
                            />
                            <List sx={{ width: 350, maxWidth: 350 }}>
                            {
                                similarProblems.length > 0 && similarProblems?.map((problem, index) => {
                                    let siteLogo = null;
                                    if(problem.site === 'CODECHEF') {
                                        siteLogo = CodechefLogo;
                                    }
                                    else if(problem.site === 'CODEFORCES') {
                                        siteLogo = CodeforcesLogo;
                                    }
                                    else if(problem.site === 'MENTORPICK') {
                                        siteLogo = MentorpickLogo;
                                    }
                                    else if(problem.site === 'LEETCODE') {
                                        siteLogo = LeetcodeLogo;
                                    }
                                    return (
                                        <ListItem key={index} disablePadding sx={{ border: "1px solid #ccc"}}>
                                            <ListItemButton>
                                                <ListItemText primary={problem.title} primaryTypographyProps={{ style: {overflow: 'scroll' , whiteSpace: 'nowrap', marginRight: '10px'}}} />
                                                <img src={ siteLogo } alt="site_logo" width={"30px"} style={{marginRight:'10px'}}/>
                                                <DeleteIcon sx={{ color: 'red' }} onClick={() => removeSimilarProblemRelationship(problem)} />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })
                            }
                            </List>
                        </div>
                    </div>
                </div>
                : 
                <>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2> Problem Details </h2>
                        <button onClick={handleReFetchUpdate} className="btn btn-primary">Re Fetch</button>
                    </div>
                    <div >
                        <label>Editorial : </label>
                        {
                            formData.editorial.length > 0 && (
                                <div className='mx-3'>
                                    {formData.editorial.map((link, index) => (
                                        <li> <a key={index} href={link} target="_blank">
                                            {link}
                                        </a>
                                            <RxCross2 style={{ cursor: "pointer" }} color='red' className='m-2' onClick={() => {
                                                handleEditorialDelete(index);
                                            }} />
                                        </li>
                                    ))}
                                </div>
                            ) || (
                                <> No editorials found </>
                            )
                        }
                        <input
                            type="text"
                            className="form-control mt-3"
                            name="editorial"
                            placeholder='Add Editorial Link'
                            onKeyDown={handleEditorialChange}
                        />
                    </div>

                    <form onSubmit={handleSubmit}>

                        <div className="form-group">
                            <label>Problem Name:</label>
                            <input
                                type="text"
                                className="form-control"
                                name="problemName"
                                value={formData.problemName}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Problem ID:</label>
                            <input
                                disabled
                                type="text"
                                className="form-control"
                                name="problemId"
                                value={formData.problemId}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Rating:</label>
                            <input
                                type="number"
                                className="form-control"
                                name="rating"
                                value={formData.rating}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Site Name:</label>
                            <input
                                disabled
                                type="text"
                                className="form-control"
                                name="siteName"
                                value={formData.siteName}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Difficulty:</label>
                            <input
                                disabled
                                type="string"
                                className="form-control"
                                name="difficulty"
                                value={formData.difficulty}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>Score:</label>
                            <input
                                type="number"
                                className="form-control"
                                name="score"
                                value={formData.score}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Original Tags:</label>
                            <Select
                                isDisabled={true}
                                isMulti
                                name="tags"
                                className="basic-multi-select"
                                classNamePrefix="select"
                                value={data?.problem?.originalTag?.map((tag) => { return { value: tag, label: tag } })}
                            />
                        </div>

                        <div className="form-group">
                            <label>Normalized Tags:</label>
                            <Select
                                isMulti
                                name="tags"
                                options={allTags}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                value={formData.normalizedTag}
                                onChange={handleTagChange}
                            />
                        </div>
                        <div className="form-group mt-3">
                            <h6>  <label>Authored Date - </label> {new Date(formData?.authoredDate).toLocaleString()} </h6>
                        </div>

                        <button type="submit" className="btn btn-primary mt-3" >Update Problem</button>
                    </form>
                </>
            }
        </div>
    );
}
